import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Search, UserPlus, Loader2, Truck } from 'lucide-react';
import { api } from '../lib/auth';
import { toast } from 'sonner';

/**
 * Türkçe-uyumlu arama normalizasyonu — VehicleAssignDialog ile aynı.
 */
function normalizeTr(s) {
  if (!s) return '';
  return String(s)
    .toLocaleLowerCase('tr')
    .replace(/[ığ]/g, 'g')
    .replace(/[üû]/g, 'u')
    .replace(/[şŝ]/g, 's')
    .replace(/[ı]/g, 'i')
    .replace(/[öô]/g, 'o')
    .replace(/[çĉ]/g, 'c')
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

/**
 * Toplu Şoför Atama dialog'u — N araç → 1 çalışan.
 *
 * Akış:
 *   1. Operatör araç listesinde checkbox ile birden fazla aracı seçer.
 *   2. "Toplu Şoför Ata" butonuna basar → bu dialog açılır.
 *   3. Tek bir çalışan seçer + opsiyonel not yazar.
 *   4. "Hepsini Ata" butonuna basar → tek API çağrısıyla tüm araçlar atanır.
 *      Var olan atamalar otomatik kapatılır, yeni history kayıtları açılır.
 */
export function BulkVehicleAssignDialog({ open, onClose, vehicles = [], onAssigned }) {
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [note, setNote] = useState('');
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSearch('');
    setSelectedId(null);
    setNote('');
    setLoading(true);
    api.get('/planning/employees')
      .then((res) => {
        const items = (res.data?.data || []).filter(
          (e) => (e.email || '').toLowerCase() !== 'admin@hrportal.be'
        );
        setEmployees(items);
      })
      .catch(() => toast.error('Çalışan listesi yüklenemedi'))
      .finally(() => setLoading(false));
  }, [open]);

  const filtered = useMemo(() => {
    const q = normalizeTr(search.trim());
    if (!q) return employees;
    return employees.filter((e) => {
      const blob = `${e.full_name || ''} ${e.email || ''} ${e.firm_name || ''} ${e.depo || ''}`;
      return normalizeTr(blob).includes(q);
    });
  }, [employees, search]);

  // Var olan atamalar (operatöre uyarı amaçlı). Vehicles tablosunda mirror'lanan
  // current_driver_name kolonunu kullanıyoruz.
  const alreadyAssigned = vehicles.filter((v) => v.current_driver_id);

  const handleAssign = async () => {
    if (!selectedId || vehicles.length === 0) return;
    setSubmitting(true);
    try {
      const res = await api.post('/vehicles/bulk-assign', {
        vehicle_ids: vehicles.map((v) => v.id),
        user_id: selectedId,
        note: note.trim() || undefined,
      });
      const data = res.data?.data || {};
      const okCount = data.assigned_count || 0;
      const skipCount = (data.skipped || []).length;
      if (skipCount > 0) {
        toast.warning(`${data.user_full_name || 'Çalışan'} → ${okCount} araç atandı, ${skipCount} araç atlandı (yetki/bulunamadı)`);
      } else {
        toast.success(`${data.user_full_name || 'Çalışan'} → ${okCount} araç başarıyla atandı`);
      }
      onAssigned?.(data);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Toplu atama yapılamadı');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-[560px]" data-testid="bulk-vehicle-assign-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-emerald-500" />
            Toplu Şoför Ata
            <Badge variant="secondary" className="ml-1" data-testid="bulk-vehicle-count">
              <Truck className="h-3 w-3 mr-1" />{vehicles.length} araç
            </Badge>
          </DialogTitle>
          <DialogDescription>
            Seçili tüm plakalar aynı çalışana atanacak. Mevcut atamalar otomatik olarak kapanır.
          </DialogDescription>
        </DialogHeader>

        {/* Seçili plakalar — kompakt chips */}
        <div className="border border-slate-200 dark:border-slate-700 rounded-md p-2 max-h-24 overflow-y-auto" data-testid="bulk-vehicle-plates">
          <div className="flex flex-wrap gap-1.5">
            {vehicles.map((v) => (
              <Badge
                key={v.id}
                variant="outline"
                className={`font-mono text-[11px] ${v.current_driver_id ? 'border-amber-300 text-amber-700 bg-amber-50 dark:bg-amber-900/20' : ''}`}
                title={v.current_driver_id ? `Şu an: ${v.current_driver_name} — yeniden atanacak` : ''}
              >
                {v.license_plate}
              </Badge>
            ))}
          </div>
          {alreadyAssigned.length > 0 && (
            <div className="text-[10px] text-amber-700 dark:text-amber-400 mt-1.5">
              {alreadyAssigned.length} plakanın mevcut ataması var — yeniden atama yapılacak.
            </div>
          )}
        </div>

        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="İsim, e-posta, firma veya depo ara…"
            className="pl-8 h-9 text-sm"
            data-testid="bulk-vehicle-assign-search"
            autoFocus
          />
        </div>

        <div className="max-h-64 overflow-y-auto border border-slate-200 dark:border-slate-700 rounded-md divide-y divide-slate-100 dark:divide-slate-700/50" data-testid="bulk-vehicle-assign-employee-list">
          {loading ? (
            <div className="py-8 text-center text-xs text-slate-400">
              <Loader2 className="h-4 w-4 animate-spin mx-auto mb-1" />
              Yükleniyor…
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-8 text-center text-xs text-slate-400 italic">
              {search ? 'Eşleşme yok' : 'Çalışan yok'}
            </div>
          ) : (
            filtered.slice(0, 100).map((emp) => {
              const sel = selectedId === emp.id;
              return (
                <button
                  key={emp.id}
                  type="button"
                  onClick={() => setSelectedId(emp.id)}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-700/40 flex items-center gap-2 ${sel ? 'bg-emerald-50 dark:bg-emerald-900/20 ring-1 ring-emerald-400' : ''}`}
                  data-testid={`bulk-vehicle-assign-pick-${emp.id}`}
                >
                  <div className={`h-7 w-7 rounded-full flex items-center justify-center text-[11px] font-bold shrink-0 ${sel ? 'bg-emerald-500 text-white' : 'bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300'}`}>
                    {(emp.full_name || '?').split(' ').slice(0, 2).map((s) => s[0]).join('').toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-slate-800 dark:text-slate-100 truncate">{emp.full_name}</div>
                    <div className="text-[11px] text-slate-500 truncate">
                      {emp.firm_name || '—'}{emp.depo ? ` · ${emp.depo}` : ''}
                      {emp.role && emp.role !== 'EMPLOYEE' && (
                        <span className="ml-1 px-1 py-0.5 rounded bg-slate-100 dark:bg-slate-700 text-[9px] uppercase font-bold">{emp.role}</span>
                      )}
                    </div>
                  </div>
                </button>
              );
            })
          )}
          {filtered.length > 100 && (
            <div className="py-2 text-center text-[10px] text-slate-400 italic">
              {filtered.length - 100} sonuç daha — daha spesifik arama yapın
            </div>
          )}
        </div>

        <div>
          <label className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">Not (opsiyonel)</label>
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Örn: Yaz sezonu rotasyonu"
            className="text-sm h-16 resize-none"
            data-testid="bulk-vehicle-assign-note"
          />
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" size="sm" onClick={onClose} disabled={submitting}>
            İptal
          </Button>
          <Button
            size="sm"
            onClick={handleAssign}
            disabled={!selectedId || submitting || vehicles.length === 0}
            data-testid="bulk-vehicle-assign-confirm"
          >
            {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <UserPlus className="h-3.5 w-3.5 mr-1" />}
            {vehicles.length} Aracı Ata
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
